class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._lastRefreshAt = 0;
    this._csQtyBusy = false;
    this._refreshPromise = null;
    this._scheduledRefreshTimer = null;
    this._scheduledRefreshOpen = false;
    this._pendingMutations = new Set();
    this._mutationQueue = Promise.resolve();
    this._groupedMutationDraft = new Map();
    this._groupedFlushTimer = null;
    this._groupedFlushPromise = null;
    this._groupedActiveFlushPromise = null;
    this._deferredRefreshTimer = null;
    this._groupedMutationVersion = 0;
    this._pendingSyncStorageKey = 'cs_cart_pending_grouped_sync_v1';
    this._cachedEmptyStateMarkup = '';
    this._primeEmptyMarkupPromise = null;
    this._awaitingCheckoutSubmit = false;
    this._priceLoadingPendingKeys = new Set();
    this._priceLoadingReleaseTimer = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Grouped qty/remove controls (same-variant lines rendered as one entry)
    this.addEventListener('click', (event) => {
      const qtyButton = event.target.closest('[data-cs-qty-action]');
      const removeButton = event.target.closest('[data-cs-remove-group]');
      if (qtyButton) this.onGroupedQtyClick(event, qtyButton);
      if (removeButton) this.onGroupedRemoveClick(event, removeButton);

      const checkoutBtn = event.target.closest('#CartDrawer-Checkout, button[name="checkout"][form="CartDrawer-Form"]');
      if (checkoutBtn) {
        event.preventDefault();
        event.stopPropagation();
        this.startCheckoutFlow(checkoutBtn);
      }
    });

    // Returning from checkout (or any back/forward navigation) can leave drawer DOM stale.
    // Refresh sections silently on pageshow (covers bfcache + normal navigation).
    window.addEventListener('pageshow', (event) => {
      // iOS bfcache can restore a stale disabled/busy checkout button state.
      this._awaitingCheckoutSubmit = false;
      const checkoutBtn = this.querySelector('#CartDrawer-Checkout');
      if (checkoutBtn) {
        checkoutBtn.removeAttribute('disabled');
        checkoutBtn.removeAttribute('aria-busy');
      }
      this.requestRefresh();
    });

    // Custom scripts (apps/custom ATC) may dispatch this instead of Dawn pubsub.
    document.addEventListener('cart:updated', (event) => {
      if (event?.detail?.skipDrawerRefresh) return;
      const suppressUntil = parseInt(this.dataset.psmSuppressRefreshUntil || '0', 10) || 0;
      if (Date.now() < suppressUntil) return;
      this.requestRefresh();
    });

    this._onCheckoutSubmitBound = this.onCheckoutSubmit.bind(this);
    this.addEventListener('submit', this._onCheckoutSubmitBound, true);
    this._onDocumentNavIntentBound = this.onDocumentNavigationIntent.bind(this);
    document.addEventListener('click', this._onDocumentNavIntentBound, true);
    document.addEventListener('submit', this._onDocumentNavIntentBound, true);
    this._onPageHideFlushBound = this.onPageHideFlush.bind(this);
    window.addEventListener('pagehide', this._onPageHideFlushBound);
    document.addEventListener('visibilitychange', this._onPageHideFlushBound);
    this.primeEmptyStateMarkup();
    this.resumePendingSyncFromStorage();
  }

  setPriceLoadingState(isLoading) {
    this.classList.toggle('cs-cart-drawer--price-loading', Boolean(isLoading));
  }

  clearPriceLoadingRows() {
    this.querySelectorAll('[data-cs-price-loading="true"]').forEach((el) => {
      el.removeAttribute('data-cs-price-loading');
      el.removeAttribute('data-cs-price-loading-key');
    });
  }

  getPriceLoadingKeyFromItem(itemEl) {
    if (!itemEl) return `global-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const signature = this.getGroupedSignatureFromItem(itemEl);
    return signature || `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  beginPriceLoading(activeItem = null) {
    const key = this.getPriceLoadingKeyFromItem(activeItem);
    this._priceLoadingPendingKeys.add(key);
    if (this._priceLoadingReleaseTimer) {
      clearTimeout(this._priceLoadingReleaseTimer);
      this._priceLoadingReleaseTimer = null;
    }
    if (activeItem) {
      activeItem.setAttribute('data-cs-price-loading', 'true');
      activeItem.setAttribute('data-cs-price-loading-key', key);
    }
    this.setPriceLoadingState(true);
    return key;
  }

  endPriceLoading({ deferMs = 0, key = '', clearAll = false } = {}) {
    if (this._priceLoadingReleaseTimer) clearTimeout(this._priceLoadingReleaseTimer);

    if (clearAll) {
      this._priceLoadingPendingKeys.clear();
      const finalizeAll = () => {
        this.setPriceLoadingState(false);
        this.clearPriceLoadingRows();
      };
      if (deferMs > 0) {
        this._priceLoadingReleaseTimer = setTimeout(() => {
          this._priceLoadingReleaseTimer = null;
          finalizeAll();
        }, deferMs);
        return;
      }
      finalizeAll();
      return;
    }

    if (key) this._priceLoadingPendingKeys.delete(key);
    const finalize = () => {
      if (key) {
        this.querySelectorAll('[data-cs-price-loading-key]').forEach((el) => {
          if ((el.getAttribute('data-cs-price-loading-key') || '') !== String(key)) return;
          el.removeAttribute('data-cs-price-loading');
          el.removeAttribute('data-cs-price-loading-key');
        });
      }
      if (this._priceLoadingPendingKeys.size === 0) this.setPriceLoadingState(false);
    };

    if (deferMs > 0) {
      this._priceLoadingReleaseTimer = setTimeout(() => {
        this._priceLoadingReleaseTimer = null;
        finalize();
      }, deferMs);
      return;
    }
    finalize();
  }

  syncEmptyStateClass() {
    // Our CSS + focus trap logic depends on cart-drawer.is-empty, but we only
    // replace inner HTML on refresh. Keep the root class in sync with DOM.
    const itemsEl = this.querySelector('cart-drawer-items');
    const isEmpty =
      (itemsEl && itemsEl.classList.contains('is-empty')) || Boolean(this.querySelector('.cs-cart-drawer__empty'));
    this.classList.toggle('is-empty', isEmpty);
    this.setDrawerEmptyVisualState(isEmpty);
  }

  setDrawerEmptyVisualState(isEmpty) {
    const footer = this.querySelector('.drawer__footer');
    if (footer) footer.toggleAttribute('hidden', isEmpty);

    const nonEmptyUpsell = this.querySelector('cs-cart-upsell:not(.cs-cart-upsell--empty)');
    if (nonEmptyUpsell) nonEmptyUpsell.toggleAttribute('hidden', isEmpty);

    const checkoutBtn = this.querySelector('#CartDrawer-Checkout');
    if (checkoutBtn) {
      if (isEmpty) checkoutBtn.setAttribute('disabled', 'disabled');
      else checkoutBtn.removeAttribute('disabled');
    }
  }

  getScrollContainer() {
    // Dawn drawers typically scroll the inner panel; fall back to contents.
    return (
      this.querySelector('#CartDrawer-CartItems') ||
      this.querySelector('.drawer__contents') ||
      this.querySelector('.drawer__inner') ||
      this.querySelector('.drawer__contents')
    );
  }

  captureScrollPositions() {
    // Different themes/app CSS may make different elements scroll.
    const candidates = [
      '#CartDrawer',
      '.drawer__inner',
      '.drawer__contents',
      '#CartDrawer-CartItems',
      'cart-drawer-items',
    ];

    const positions = {};
    candidates.forEach((sel) => {
      const el = this.querySelector(sel);
      if (!el) return;
      positions[sel] = el.scrollTop || 0;
    });

    return positions;
  }

  restoreScrollPositions(positions) {
    if (!positions) return;
    const apply = () => {
      Object.keys(positions).forEach((sel) => {
        const el = this.querySelector(sel);
        if (!el) return;
        el.scrollTop = positions[sel];
      });
    };

    // Apply a few times to beat focus-trap / layout reflow
    requestAnimationFrame(apply);
    setTimeout(apply, 0);
    setTimeout(apply, 60);
    setTimeout(apply, 140);
  }

  updateFooterHeightVar() {
    const footer = this.querySelector('.drawer__footer');
    const height = footer ? footer.offsetHeight : 0;
    this.style.setProperty('--cs-cart-drawer-footer-height', `${height}px`);
  }

  readCartBubbleCount() {
    const bubble = document.querySelector('#cart-icon-bubble');
    if (!bubble) return 0;
    const countEl =
      bubble.querySelector('.cart-count-bubble span[aria-hidden="true"]') ||
      bubble.querySelector('.cart-count-bubble');
    const text = (countEl?.textContent || bubble.textContent || '').trim();
    const n = parseInt((text.match(/\d+/) || [0])[0], 10);
    return Number.isFinite(n) ? n : 0;
  }

  setCartBubbleCount(nextCount) {
    const bubble = document.querySelector('#cart-icon-bubble');
    if (!bubble) return;
    const count = Math.max(0, parseInt(nextCount, 10) || 0);
    let container = bubble.querySelector('.cart-count-bubble');
    let visibleCount = bubble.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    let srCount = bubble.querySelector('.cart-count-bubble span.visually-hidden');

    // Empty-cart header markup can lack the bubble node entirely.
    // Create it on-demand so optimistic count updates are always visible.
    if (!container && count > 0) {
      container = document.createElement('div');
      container.className = 'cart-count-bubble';

      visibleCount = document.createElement('span');
      visibleCount.setAttribute('aria-hidden', 'true');
      container.appendChild(visibleCount);

      srCount = document.createElement('span');
      srCount.className = 'visually-hidden';
      container.appendChild(srCount);

      bubble.appendChild(container);
    }

    if (visibleCount) visibleCount.textContent = String(count);
    if (srCount) srCount.textContent = `${count} items`;
    if (container) container.style.display = count > 0 ? '' : 'none';
  }

  adjustCartBubbleCount(delta) {
    const current = this.readCartBubbleCount();
    this.setCartBubbleCount(current + (parseInt(delta, 10) || 0));
  }

  cacheEmptyStateMarkupFromHtml(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const emptyNode = doc.querySelector('#CartDrawer .drawer__inner-empty');
    if (emptyNode) this._cachedEmptyStateMarkup = emptyNode.outerHTML;
  }

  async primeEmptyStateMarkup() {
    if (this._cachedEmptyStateMarkup) return;
    if (this._primeEmptyMarkupPromise) return this._primeEmptyMarkupPromise;
    this._primeEmptyMarkupPromise = fetch('/?sections=cart-drawer', { credentials: 'same-origin', cache: 'no-store' })
      .then((res) => res.json())
      .then((sections) => {
        this.cacheEmptyStateMarkupFromHtml(sections?.['cart-drawer']);
      })
      .catch(() => {})
      .finally(() => {
        this._primeEmptyMarkupPromise = null;
      });
    return this._primeEmptyMarkupPromise;
  }

  applyOptimisticEmptyStateIfNeeded() {
    const items = this.querySelectorAll('.cs-cart-item');
    if (items.length > 0) return false;

    const cartItems = this.querySelector('cart-drawer-items');
    const contents = this.querySelector('#CartDrawer-CartItems');
    if (!cartItems || !contents) return false;
    if (contents.querySelector('.drawer__inner-empty')) {
      this.classList.add('is-empty');
      cartItems.classList.add('is-empty');
      this.setDrawerEmptyVisualState(true);
      return true;
    }

    const template = this.querySelector('#CartDrawer-EmptyStateTemplate');
    contents.innerHTML = '';
    if (template && template.content && template.content.firstElementChild) {
      contents.appendChild(template.content.cloneNode(true));
    } else {
      const html = this._cachedEmptyStateMarkup
        ? this._cachedEmptyStateMarkup
        : `
          <div class="drawer__inner-empty cs-cart-drawer__empty">
            <button type="button" class="button cs-cart-drawer__continue">Continue shopping</button>
            <cs-cart-upsell class="cs-cart-upsell cs-cart-upsell--empty">
              <div class="cs-cart-upsell__box">
                <div class="cs-cart-upsell__top">
                  <div class="cs-cart-upsell__heading">You may also like...</div>
                </div>
                <div class="cs-cart-upsell__slider" role="region" aria-label="You may also like">
                  <div class="cs-cart-upsell__track cs-cart-upsell__grid" role="list"></div>
                </div>
              </div>
            </cs-cart-upsell>
          </div>
        `;
      contents.innerHTML = html;
    }
    const continueBtn = contents.querySelector('.cs-cart-drawer__continue');
    if (continueBtn) continueBtn.addEventListener('click', () => this.close());

    this.classList.add('is-empty');
    cartItems.classList.add('is-empty');
    const heading = this.querySelector('.cs-cart-drawer__heading');
    if (heading) heading.textContent = 'Your cart is empty';
    this.setDrawerEmptyVisualState(true);
    return true;
  }

  setBusyUI(isBusy, activeItem = null) {
    this.classList.toggle('cs-cart-drawer--busy', isBusy);
    this.toggleAttribute('aria-busy', isBusy);

    // Clear previous item loading state
    this.querySelectorAll('[data-cs-loading="true"]').forEach((el) => el.removeAttribute('data-cs-loading'));

    // Mark active cart entry as loading (CSS shows overlay + spinner)
    if (isBusy && activeItem) activeItem.setAttribute('data-cs-loading', 'true');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Ensure we don't show a stale cart (e.g. after editing cart page or returning from checkout).
    const hasPendingWork = this._pendingMutations.size || this.hasPendingGroupedWork();
    if (!hasPendingWork && Date.now() - this._lastRefreshAt > 10000) this.requestRefresh();

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.updateFooterHeightVar();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);

        // Focus trapping can jump scroll; re-apply if pending.
        if (this._pendingScrollPositions) {
          this.restoreScrollPositions(this._pendingScrollPositions);
          this._pendingScrollPositions = null;
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    // Ensure drawer edits are committed as soon as user leaves the drawer,
    // so subsequent add-to-cart actions from elsewhere don't race pending drafts.
    if (this.hasPendingGroupedWork() || this._pendingMutations.size) {
      this.flushAllPendingNow().catch(() => {});
    }
  }

  requestRefresh({ open = false, immediate = false } = {}) {
    this._scheduledRefreshOpen = this._scheduledRefreshOpen || open;

    const run = () => {
      const shouldOpen = this._scheduledRefreshOpen;
      this._scheduledRefreshOpen = false;
      return this.refresh({ open: shouldOpen });
    };

    if (immediate) {
      if (this._scheduledRefreshTimer) {
        clearTimeout(this._scheduledRefreshTimer);
        this._scheduledRefreshTimer = null;
      }
      return run();
    }

    if (this._scheduledRefreshTimer) return this._refreshPromise || Promise.resolve();

    // Coalesce bursts of cart updates into a single section refresh.
    this._scheduledRefreshTimer = setTimeout(() => {
      this._scheduledRefreshTimer = null;
      run();
    }, 45);

    return this._refreshPromise || Promise.resolve();
  }

  trackMutation(promiseLike) {
    const tracked = Promise.resolve(promiseLike).finally(() => {
      this._pendingMutations.delete(tracked);
    });
    this._pendingMutations.add(tracked);
    return tracked;
  }

  queueMutation(task) {
    const run = this._mutationQueue
      .catch(() => {})
      .then(() => task());
    this._mutationQueue = run.catch(() => {});
    return this.trackMutation(run);
  }

  async waitForPendingMutations() {
    while (this._pendingMutations.size) {
      const pending = Array.from(this._pendingMutations);
      await Promise.allSettled(pending);
    }
  }

  hasPendingGroupedWork() {
    return (
      this._groupedMutationDraft.size ||
      Boolean(this._groupedFlushTimer) ||
      Boolean(this._groupedFlushPromise) ||
      Boolean(this._groupedActiveFlushPromise)
    );
  }

  hasPendingCartMutations() {
    return this.hasPendingGroupedWork() || this._pendingMutations.size > 0;
  }

  collectGroupedDraftFromDOM() {
    const drafts = new Map();
    this.querySelectorAll('.cs-cart-item[data-cs-variant-id]').forEach((item) => {
      const signature = this.getGroupedSignatureFromItem(item);
      if (!signature) return;
      const box = item.matches('[data-cs-group-keys]') ? item : item.querySelector('[data-cs-group-keys]');
      if (!box) return;
      const { keys, qtys } = this.parseGroupDataset(box);
      if (!keys.length || keys.length !== qtys.length) return;
      const desiredTotal = qtys.reduce((acc, n) => acc + (parseInt(n, 10) || 0), 0);
      drafts.set(signature, Math.max(0, desiredTotal));
    });
    return drafts;
  }

  readPendingSyncFromStorage() {
    const parsePayload = (raw) => {
      if (!raw) return null;
      try {
        const data = JSON.parse(raw);
        if (!data || !Array.isArray(data.drafts)) return null;
        if (Date.now() - (parseInt(data.ts, 10) || 0) > 15 * 60 * 1000) return null;
        return data;
      } catch (_) {
        return null;
      }
    };

    try {
      const local = parsePayload(localStorage.getItem(this._pendingSyncStorageKey));
      if (local) return local;
    } catch (_) {}

    try {
      return parsePayload(sessionStorage.getItem(this._pendingSyncStorageKey));
    } catch (_) {
      return null;
    }
  }

  clearPendingSyncStorage() {
    try {
      localStorage.removeItem(this._pendingSyncStorageKey);
    } catch (_) {}
    try {
      sessionStorage.removeItem(this._pendingSyncStorageKey);
    } catch (_) {}
  }

  persistPendingSyncToStorage() {
    if (!this.hasPendingCartMutations()) {
      this.clearPendingSyncStorage();
      return;
    }

    const merged = this.collectGroupedDraftFromDOM();
    this._groupedMutationDraft.forEach((qty, signature) => {
      merged.set(signature, Math.max(0, parseInt(qty, 10) || 0));
    });
    if (!merged.size) return;

    const payload = JSON.stringify({
      v: 1,
      ts: Date.now(),
      drafts: Array.from(merged.entries()),
    });

    try {
      localStorage.setItem(this._pendingSyncStorageKey, payload);
      return;
    } catch (_) {}

    try {
      sessionStorage.setItem(this._pendingSyncStorageKey, payload);
    } catch (_) {}
  }

  resumePendingSyncFromStorage() {
    const data = this.readPendingSyncFromStorage();
    if (!data) return;
    this.clearPendingSyncStorage();

    let restored = 0;
    data.drafts.forEach((entry) => {
      if (!Array.isArray(entry) || entry.length < 2) return;
      const signature = String(entry[0] || '');
      if (!signature) return;
      const qty = Math.max(0, parseInt(entry[1], 10) || 0);
      this._groupedMutationDraft.set(signature, qty);
      restored += 1;
    });

    if (!restored) return;
    this._groupedMutationVersion += 1;
    this.scheduleGroupedFlush();
    this.requestRefreshAfterGroupedSettles();
  }

  requestRefreshAfterGroupedSettles() {
    if (this._deferredRefreshTimer) return;
    this._deferredRefreshTimer = setTimeout(() => {
      this._deferredRefreshTimer = null;
      if (this.hasPendingGroupedWork() || this._pendingMutations.size) {
        this.requestRefreshAfterGroupedSettles();
        return;
      }
      this.clearPendingSyncStorage();
      this.requestRefresh({ open: this.classList.contains('active'), immediate: true });
    }, 20);
  }

  queueGroupedUpdatesFromBox(box, { immediate = false } = {}) {
    if (!box) return;
    const { keys, qtys } = this.parseGroupDataset(box);
    if (!keys.length || keys.length !== qtys.length) return;
    const item = box.closest('.cs-cart-item');
    const signature = this.getGroupedSignatureFromItem(item);
    if (!signature) return;
    const desiredTotal = qtys.reduce((acc, n) => acc + (parseInt(n, 10) || 0), 0);
    this._groupedMutationDraft.set(signature, Math.max(0, desiredTotal));
    this.persistPendingSyncToStorage();
    this._groupedMutationVersion += 1;
    if (this._deferredRefreshTimer) {
      clearTimeout(this._deferredRefreshTimer);
      this._deferredRefreshTimer = null;
    }
    if (immediate) {
      this.flushGroupedMutations().catch(() => {
        this.scheduleGroupedFlush();
      });
      return;
    }
    this.scheduleGroupedFlush();
  }

  scheduleGroupedFlush() {
    if (this._groupedFlushTimer || this._groupedFlushPromise || this._groupedActiveFlushPromise) {
      return this._groupedActiveFlushPromise || this._groupedFlushPromise || Promise.resolve();
    }

    this._groupedFlushPromise = new Promise((resolve) => {
      this._groupedFlushTimer = setTimeout(async () => {
        this._groupedFlushTimer = null;
        try {
          await this.flushGroupedMutations();
        } catch (_) {
          // keep unsent updates queued and retry shortly
          this.scheduleGroupedFlush();
        } finally {
          const hasQueuedDraft = this._groupedMutationDraft.size > 0;
          this._groupedFlushPromise = null;
          resolve();
          // If a new draft arrived while the previous flush was running,
          // immediately queue another flush so prices/totals stay in sync.
          if (hasQueuedDraft) this.scheduleGroupedFlush();
        }
      }, 40);
    });

    return this._groupedFlushPromise;
  }

  async flushGroupedMutations() {
    if (this._groupedActiveFlushPromise) return this._groupedActiveFlushPromise;

    const active = this.flushGroupedMutationsWithOptions()
      .catch((err) => {
        throw err;
      })
      .finally(() => {
        if (this._groupedActiveFlushPromise === active) {
          this._groupedActiveFlushPromise = null;
        }
        // Drafts can arrive while an immediate flush is in flight; ensure
        // they are not stranded and get flushed right after this run.
        if (this._groupedMutationDraft.size && !this._groupedFlushTimer && !this._groupedFlushPromise) {
          this.scheduleGroupedFlush();
        }
      });

    this._groupedActiveFlushPromise = active;
    return active;
  }

  async flushGroupedMutationsWithOptions({ keepalive = false } = {}) {
    if (!this._groupedMutationDraft.size) return;
    const flushVersion = this._groupedMutationVersion;
    const desiredBySignature = new Map(this._groupedMutationDraft);
    this._groupedMutationDraft.clear();

    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then((r) => r.json());
      const items = Array.isArray(cart?.items) ? cart.items : [];

      const updates = {};
      const additions = [];

      desiredBySignature.forEach((desiredQty, signature) => {
        const sig = this.parseGroupedSignature(signature);
        if (!sig) return;
        const lines = this.getCartLinesForGroupedSignature(items, sig);
        const currentTotal = lines.reduce((a, it) => a + (it.quantity || 0), 0);
        const desired = Math.max(0, parseInt(desiredQty, 10) || 0);

        if (desired <= currentTotal) {
          let remaining = desired;
          lines.forEach((it) => {
            const next = Math.max(0, Math.min(it.quantity || 0, remaining));
            updates[it.key] = next;
            remaining -= next;
          });
          return;
        }

        // desired > current
        const delta = desired - currentTotal;
        if (lines.length) {
          const first = lines[0];
          updates[first.key] = (first.quantity || 0) + delta;
          return;
        }

        // Group missing entirely in current cart -> add fresh lines for this signature.
        additions.push({ sig, delta });
      });

      if (Object.keys(updates).length) {
        if (keepalive) {
          const req = fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-store',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates }),
          });
          await this.trackMutation(req);
        } else {
          await this.updateManyLineItems(updates);
        }
      }

      if (!keepalive && additions.length) {
        for (const add of additions) {
          if (!add.sig.variantId || add.delta <= 0) continue;
          if (add.sig.purchaseType === 'subscription' && !add.sig.sellingPlanId) continue;
          await this.addVariant(add.sig.variantId, add.sig.sellingPlanId, { withSections: false, quantity: add.delta });
        }
      }

      if (!keepalive && flushVersion === this._groupedMutationVersion) {
        this.requestRefreshAfterGroupedSettles();
      }
    } catch (err) {
      // Re-queue desired grouped totals so checkout/navigation can retry flush.
      desiredBySignature.forEach((qty, signature) => {
        this._groupedMutationDraft.set(signature, qty);
      });
      this.persistPendingSyncToStorage();
      this.endPriceLoading({ clearAll: true });
      throw err;
    }
  }

  getGroupedSignatureFromItem(itemEl) {
    if (!itemEl) return '';
    const variantId = parseInt(itemEl.dataset.csVariantId || '', 10);
    const sellingPlanId = String(itemEl.dataset.csSellingPlanId || '');
    const purchaseType = String(itemEl.dataset.csPurchaseType || 'one-time').toLowerCase();
    if (!variantId) return '';
    return `${variantId}::${sellingPlanId}::${purchaseType}`;
  }

  parseGroupedSignature(signature) {
    if (!signature) return null;
    const parts = String(signature).split('::');
    if (parts.length < 3) return null;
    const variantId = parseInt(parts[0], 10);
    if (!variantId) return null;
    return {
      variantId,
      sellingPlanId: parts[1] || '',
      purchaseType: (parts[2] || 'one-time').toLowerCase(),
    };
  }

  isSubscriptionFromProperties(props) {
    if (!props) return false;
    const keys = Object.keys(props);
    for (const k of keys) {
      const kk = String(k).toLowerCase();
      if (
        kk.includes('shipping_interval_') ||
        kk.includes('order_interval_') ||
        kk.includes('charge_interval_') ||
        kk.includes('recharge') ||
        kk.includes('subscription') ||
        kk.includes('selling_plan')
      ) {
        return true;
      }
    }
    return false;
  }

  getCartLinesForGroupedSignature(items, sig) {
    return items.filter((it) => {
      if (it.variant_id !== sig.variantId) return false;
      const itSpid = it.selling_plan_allocation?.selling_plan?.id ? String(it.selling_plan_allocation.selling_plan.id) : '';
      if (String(sig.sellingPlanId)) return itSpid === String(sig.sellingPlanId);
      if (sig.purchaseType === 'subscription') return this.isSubscriptionFromProperties(it.properties) || Boolean(it.selling_plan_allocation);
      if (sig.purchaseType === 'one-time') return !this.isSubscriptionFromProperties(it.properties) && !it.selling_plan_allocation;
      return itSpid === '';
    });
  }

  async flushAllPendingNow({ keepalive = false } = {}) {
    if (!this.hasPendingCartMutations()) return;
    if (this._groupedFlushTimer) {
      clearTimeout(this._groupedFlushTimer);
      this._groupedFlushTimer = null;
    }
    if (this._groupedMutationDraft.size) {
      await this.flushGroupedMutationsWithOptions({ keepalive });
    }
    await this.waitForPendingMutations();
    this.clearPendingSyncStorage();
  }

  async onDocumentNavigationIntent(event) {
    if (!this.hasPendingGroupedWork() && !this._pendingMutations.size) return;
    if (this._awaitingCheckoutSubmit) return;

    if (event.type === 'click') {
      const link = event.target?.closest?.('a[href]');
      if (!link) return;
      // Header cart icon is an anchor to /cart; keep drawer-open behavior only.
      if (link.id === 'cart-icon-bubble' || link.closest('#cart-icon-bubble')) return;
      if (link.target === '_blank' || link.hasAttribute('download')) return;
      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      this.persistPendingSyncToStorage();
      this.flushAllPendingNow({ keepalive: true }).catch(() => {});
      return;
    }

    if (event.type === 'submit') {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.id === 'CartDrawer-Form') return; // handled by dedicated checkout guard
      this.persistPendingSyncToStorage();
      this.flushAllPendingNow({ keepalive: true }).catch(() => {});
    }
  }

  onPageHideFlush(evt) {
    if (evt?.type === 'visibilitychange' && document.visibilityState !== 'hidden') return;
    if (!this.hasPendingGroupedWork() && !this._pendingMutations.size) return;
    this.persistPendingSyncToStorage();
    // Best-effort fire before unload/navigation teardown.
    this.flushAllPendingNow({ keepalive: true }).catch(() => {});
  }

  async onCheckoutSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.id !== 'CartDrawer-Form') return;

    const submitter = event.submitter || document.activeElement;
    const isCheckoutSubmitter =
      submitter &&
      (submitter.name === 'checkout' || submitter.id === 'CartDrawer-Checkout' || submitter.getAttribute('form') === 'CartDrawer-Form');
    if (!isCheckoutSubmitter) return;
    event.preventDefault();
    this.startCheckoutFlow(submitter);
  }

  async startCheckoutFlow(submitter) {
    const hasPendingWork = this._pendingMutations.size || this.hasPendingGroupedWork();
    if (this._awaitingCheckoutSubmit) return;

    this._awaitingCheckoutSubmit = true;
    if (submitter) {
      submitter.setAttribute('aria-busy', 'true');
      submitter.setAttribute('disabled', 'disabled');
    }

    try {
      if (hasPendingWork) {
        if (this._groupedFlushTimer) {
          clearTimeout(this._groupedFlushTimer);
          this._groupedFlushTimer = null;
        }
        if (this._groupedMutationDraft.size) await this.flushGroupedMutations();
        await this.waitForPendingMutations();
        if (this._groupedMutationDraft.size) {
          await this.flushGroupedMutations();
          await this.waitForPendingMutations();
        }
      }
      window.location.assign('/checkout');
    } finally {
      this._awaitingCheckoutSubmit = false;
      if (submitter) {
        submitter.removeAttribute('aria-busy');
        submitter.removeAttribute('disabled');
      }
    }
  }

  refresh({ open = false } = {}) {
    // Serialize refreshes so a refresh requested during an in-flight refresh is NOT dropped.
    const prev = this._refreshPromise || Promise.resolve();
    this._refreshPromise = prev.finally(() => this._doRefresh({ open }));
    return this._refreshPromise;
  }

  _doRefresh({ open = false } = {}) {
    const previousScrollPositions = this.captureScrollPositions();
    const wasActive = this.classList.contains('active');
    this._pendingScrollPositions = previousScrollPositions;
    // Refresh drawer + cart bubble without forcing open.
    return fetch('/?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin' })
      .then((res) => res.json())
      .then((sections) => {
        const parsedState = { id: this.productId, sections };
        this.cacheEmptyStateMarkupFromHtml(parsedState.sections['cart-drawer']);
        this.getSectionsToRender().forEach((section) => {
          if (section.id === 'cart-drawer') {
            this.patchCartDrawerSection(parsedState.sections[section.id]);
            return;
          }
          const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
          if (!sectionElement) return;
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        });

        // Rebind overlay click (DOM replaced)
        const overlay = this.querySelector('#CartDrawer-Overlay');
        overlay && overlay.addEventListener('click', this.close.bind(this));

        // Prevent open() from triggering another refresh due to staleness heuristics.
        this._lastRefreshAt = Date.now();
        this.syncEmptyStateClass();
        this.updateFooterHeightVar();
        this.restoreScrollPositions(previousScrollPositions);
        // Don't re-open if already open; it can reset scroll via focus-trap.
        if (open && !wasActive) this.open();
      })
      .catch(() => {})
      .finally(() => {
        this._lastRefreshAt = Date.now();
        this.syncEmptyStateClass();
        this.updateFooterHeightVar();
        this.restoreScrollPositions(previousScrollPositions);
        this.endPriceLoading({ deferMs: 60, clearAll: true });
      });
  }

  parseGroupDataset(el) {
    const keys = (el.dataset.csGroupKeys || '').split('||').filter(Boolean);
    const qtys = (el.dataset.csGroupQtys || '').split('||').filter(Boolean).map((n) => parseInt(n, 10) || 0);
    return { keys, qtys };
  }

  serializeGroupQtys(qtys) {
    return qtys.map((n) => String(Math.max(0, parseInt(n, 10) || 0))).join('||');
  }

  applyOptimisticGroupedQty(box, activeItem, action) {
    const { keys, qtys } = this.parseGroupDataset(box);
    if (!keys.length || keys.length !== qtys.length) return null;

    const nextQtys = [...qtys];
    if (action === 'plus') {
      nextQtys[0] = (nextQtys[0] || 0) + 1;
    } else if (action === 'minus') {
      for (let i = nextQtys.length - 1; i >= 0; i--) {
        if ((nextQtys[i] || 0) > 0) {
          nextQtys[i] = nextQtys[i] - 1;
          break;
        }
      }
    }

    const total = nextQtys.reduce((a, n) => a + (n || 0), 0);
    box.dataset.csGroupQtys = this.serializeGroupQtys(nextQtys);

    if (activeItem) {
      const qtyCount = activeItem.querySelector('.cs-qty__count');
      if (qtyCount) qtyCount.textContent = String(total);
      const minusBtn = activeItem.querySelector('[data-cs-qty-action="minus"]');
      if (minusBtn) minusBtn.classList.toggle('disabled', total <= 1);
    }

    return { keys, qtys };
  }

  applyOptimisticGroupedRemove(box, activeItem) {
    const { keys, qtys } = this.parseGroupDataset(box);
    if (!keys.length || keys.length !== qtys.length) return null;

    const parent = activeItem?.parentElement || null;
    const nextSibling = activeItem?.nextSibling || null;
    if (activeItem) activeItem.remove();
    box.dataset.csGroupQtys = this.serializeGroupQtys(qtys.map(() => 0));

    return { keys, qtys, activeItem, parent, nextSibling };
  }

  getGroupLinesFromDOM(el) {
    if (!el) return [];
    const { keys, qtys } = this.parseGroupDataset(el);
    if (!keys.length || keys.length !== qtys.length) return [];
    return keys.map((key, i) => ({ key, quantity: qtys[i] || 0 })).filter((it) => it.key);
  }

  async requestJson(url, options = {}) {
    const res = await fetch(url, options);
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      // ignore
    }
    if (!res.ok) {
      const err = new Error(`Request failed: ${res.status}`);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  getSectionRenderPayload() {
    return {
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    };
  }

  applySectionsFromState(state) {
    if (!state || !state.sections) return false;
    this.renderContents(state);
    return true;
  }

  patchCartDrawerSection(html) {
    if (!html) return false;
    this.cacheEmptyStateMarkupFromHtml(html);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const selectors = [
      '#CartDrawer .cs-cart-drawer__header',
      '#CartDrawer cart-drawer-items',
      '#CartDrawer .drawer__footer',
    ];

    let replaced = false;
    selectors.forEach((selector) => {
      const target = this.querySelector(selector);
      const source = doc.querySelector(selector);
      if (!target || !source) return;
      target.replaceWith(source.cloneNode(true));
      replaced = true;
    });

    // Upsert non-empty upsell block; when cart goes empty->non-empty, target may not exist yet.
    const upsellSelector = '#CartDrawer cs-cart-upsell:not(.cs-cart-upsell--empty)';
    const upsellTarget = this.querySelector(upsellSelector);
    const upsellSource = doc.querySelector(upsellSelector);
    const drawerInner = this.querySelector('#CartDrawer .drawer__inner');
    const footer = this.querySelector('#CartDrawer .drawer__footer');
    if (upsellSource && upsellTarget) {
      upsellTarget.replaceWith(upsellSource.cloneNode(true));
      replaced = true;
    } else if (upsellSource && !upsellTarget && drawerInner) {
      const node = upsellSource.cloneNode(true);
      if (footer && footer.parentElement === drawerInner) drawerInner.insertBefore(node, footer);
      else drawerInner.appendChild(node);
      replaced = true;
    } else if (!upsellSource && upsellTarget) {
      upsellTarget.remove();
      replaced = true;
    }

    return replaced;
  }

  async changeLineItemByKey(key, quantity, { withSections = false } = {}) {
    const payload = { id: key, quantity };
    if (withSections) Object.assign(payload, this.getSectionRenderPayload());

    return this.queueMutation(() =>
      this.requestJson('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    );
  }

  async updateManyLineItems(updates, { withSections = false } = {}) {
    const payload = { updates };
    if (withSections) Object.assign(payload, this.getSectionRenderPayload());

    return this.queueMutation(() =>
      this.requestJson('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    );
  }

  async addVariant(variantId, sellingPlanId = '', { withSections = false, quantity = 1 } = {}) {
    if (!variantId) return;
    const payload = { id: variantId, quantity: Math.max(1, parseInt(quantity, 10) || 1) };
    if (sellingPlanId) payload.selling_plan = sellingPlanId;
    if (withSections) Object.assign(payload, this.getSectionRenderPayload());

    return this.queueMutation(() =>
      this.requestJson('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    );
  }

  async getGroupLinesFromCart(activeItem) {
    const variantId = parseInt(activeItem?.dataset?.csVariantId || '', 10);
    const sellingPlanId = activeItem?.dataset?.csSellingPlanId || '';
    const purchaseType = (activeItem?.dataset?.csPurchaseType || '').toLowerCase();
    if (!variantId) return [];

    const cart = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then((r) => r.json());
    if (!cart || !Array.isArray(cart.items)) return [];

    const isSubFromProps = (props) => {
      if (!props) return false;
      const keys = Object.keys(props);
      for (const k of keys) {
        const kk = String(k).toLowerCase();
        if (
          kk.includes('shipping_interval_') ||
          kk.includes('order_interval_') ||
          kk.includes('charge_interval_') ||
          kk.includes('recharge') ||
          kk.includes('subscription') ||
          kk.includes('selling_plan')
        ) {
          return true;
        }
      }
      return false;
    };

    return cart.items
      .filter((it) => {
        const itSpid = it.selling_plan_allocation?.selling_plan?.id ? String(it.selling_plan_allocation.selling_plan.id) : '';
        if (it.variant_id !== variantId) return false;
        if (String(sellingPlanId)) return itSpid === String(sellingPlanId);
        // No selling plan id available → split by purchase type marker
        if (purchaseType === 'subscription') return isSubFromProps(it.properties) || Boolean(it.selling_plan_allocation);
        if (purchaseType === 'one-time') return !isSubFromProps(it.properties) && !it.selling_plan_allocation;
        return itSpid === '';
      })
      .map((it) => ({ key: it.key, quantity: it.quantity }))
      .filter((it) => it.key);
  }

  async onGroupedQtyClick(event, button) {
    const box = button.closest('[data-cs-group-keys]');
    if (!box) return;
    event.preventDefault();

    const activeItem = button.closest('.cs-cart-item');

    const action = button.dataset.csQtyAction;
    const variantId = parseInt(activeItem?.dataset?.csVariantId || '', 10);
    const sellingPlanId = activeItem?.dataset?.csSellingPlanId || '';
    const purchaseType = (activeItem?.dataset?.csPurchaseType || '').toLowerCase();
    let group = this.getGroupLinesFromDOM(box);
    if (!group.length) {
      const priceLoadingKey = this.beginPriceLoading(activeItem);
      if (action === 'plus' && variantId && (sellingPlanId || purchaseType !== 'subscription')) {
        try {
          this.adjustCartBubbleCount(1);
          await this.addVariant(variantId, sellingPlanId);
          this.requestRefresh({ open: this.classList.contains('active'), immediate: true });
        } catch (_) {
          // swallow
          this.adjustCartBubbleCount(-1);
          this.endPriceLoading({ key: priceLoadingKey });
        }
      } else {
        // Metadata mismatch or stale DOM: force a hard section refresh so
        // row prices/subtotal recover to server-truth before next mutation.
        this.requestRefresh({ open: this.classList.contains('active'), immediate: true });
      }
      return;
    }

    const totalQty = group.reduce((a, it) => a + (it.quantity || 0), 0);
    if (action === 'minus' && totalQty <= 1) return;

    const priceLoadingKey = this.beginPriceLoading(activeItem);
    const optimisticSnapshot = this.applyOptimisticGroupedQty(box, activeItem, action);
    this.adjustCartBubbleCount(action === 'plus' ? 1 : -1);

    try {
      // Qty taps should feel immediate; flush grouped mutation right away.
      this.queueGroupedUpdatesFromBox(box, { immediate: true });
    } catch (e) {
      // swallow
      this.adjustCartBubbleCount(action === 'plus' ? -1 : 1);
      if (optimisticSnapshot && box) {
        box.dataset.csGroupQtys = this.serializeGroupQtys(optimisticSnapshot.qtys);
      }
      this.endPriceLoading({ key: priceLoadingKey });
    }
  }

  async onGroupedRemoveClick(event, button) {
    const box = button.closest('[data-cs-group-keys]');
    if (!box) return;
    event.preventDefault();

    const activeItem = button.closest('.cs-cart-item');
    const priceLoadingKey = this.beginPriceLoading(activeItem);
    const optimisticSnapshot = this.applyOptimisticGroupedRemove(box, activeItem);
    const isNowEmpty = this.applyOptimisticEmptyStateIfNeeded();
    const removedTotal = optimisticSnapshot?.qtys?.reduce((a, n) => a + (parseInt(n, 10) || 0), 0) || 0;
    if (removedTotal) this.adjustCartBubbleCount(-removedTotal);

    try {
      this.queueGroupedUpdatesFromBox(box, { immediate: isNowEmpty });
    } catch (e) {
      // swallow
      if (removedTotal) this.adjustCartBubbleCount(removedTotal);
      if (optimisticSnapshot?.parent && optimisticSnapshot?.activeItem) {
        if (optimisticSnapshot.nextSibling && optimisticSnapshot.nextSibling.parentNode === optimisticSnapshot.parent) {
          optimisticSnapshot.parent.insertBefore(optimisticSnapshot.activeItem, optimisticSnapshot.nextSibling);
        } else {
          optimisticSnapshot.parent.appendChild(optimisticSnapshot.activeItem);
        }
      }
      if (optimisticSnapshot?.qtys && box) {
        box.dataset.csGroupQtys = this.serializeGroupQtys(optimisticSnapshot.qtys);
      }
      this.endPriceLoading({ key: priceLoadingKey });
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const hasPendingWork = this.hasPendingGroupedWork() || this._pendingMutations.size;
    if (hasPendingWork) {
      this.requestRefreshAfterGroupedSettles();
      if (!this.classList.contains('active')) this.open();
      return;
    }

    const previousScrollPositions = this.captureScrollPositions();
    const wasActive = this.classList.contains('active');
    const skipAutoOpenOnce = this.dataset.psmSkipAutoOpenOnce === 'true';
    if (skipAutoOpenOnce) delete this.dataset.psmSkipAutoOpenOnce;
    const suppressUntil = parseInt(this.dataset.psmSuppressRefreshUntil || '0', 10) || 0;
    const suppressBubbleSection = Date.now() < suppressUntil;
    this._pendingScrollPositions = previousScrollPositions;
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      if (section.id === 'cart-icon-bubble' && suppressBubbleSection) return;
      if (section.id === 'cart-drawer') {
        this.patchCartDrawerSection(parsedState.sections[section.id]);
        return;
      }
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.syncEmptyStateClass();
      this.updateFooterHeightVar();
      this.restoreScrollPositions(previousScrollPositions);
      if (!wasActive && !skipAutoOpenOnce) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector);
    // Some responses return only the inner HTML (no wrapper matching selector)
    return node ? node.innerHTML : doc.body.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector) || doc.body.firstElementChild;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CSCartUpsell extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;
    this.drawer = this.closest('cart-drawer');
    this.renderShell();
    this.load();
    this.bind();
  }

  sortTrackByPrice(track) {
    if (!track) return;
    const items = Array.from(track.querySelectorAll('.cs-cart-upsell__item'));
    if (items.length < 2) return;

    const priorityIds = (String(this.dataset.csPriorityVariantIds || '').match(/\d+/g) || [])
      .map((s) => parseInt(s, 10))
      .filter((n) => Number.isFinite(n) && n > 0);
    const priorityRank = new Map(priorityIds.map((id, idx) => [id, idx]));

    const priceOf = (el) => {
      const raw = el?.dataset?.csPrice;
      const n = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
      return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
    };
    const variantIdOf = (el) => {
      const addBtn = el?.querySelector('[data-variant-id]');
      const n = parseInt(addBtn?.getAttribute('data-variant-id') || '', 10);
      return Number.isFinite(n) ? n : 0;
    };
    const rankOf = (el) => {
      const id = variantIdOf(el);
      return priorityRank.has(id) ? priorityRank.get(id) : Number.MAX_SAFE_INTEGER;
    };

    items
      .map((el, idx) => ({ el, idx, price: priceOf(el), rank: rankOf(el) }))
      .sort((a, b) => (a.rank - b.rank) || (a.price - b.price) || (a.idx - b.idx))
      .forEach(({ el }) => track.appendChild(el));

    // After reordering, start from the beginning.
    track.scrollLeft = 0;
  }

  updateArrowState() {
    const track = this.querySelector('.cs-cart-upsell__track');
    if (!track) return;
    const prev = this.querySelector('.cs-cart-upsell__arrow--prev');
    const next = this.querySelector('.cs-cart-upsell__arrow--next');

    // If arrows aren't present (e.g. empty-state grid), nothing to do.
    if (!prev || !next) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const x = track.scrollLeft;
    const epsilon = 2; // tolerate sub-pixel / momentum rounding

    const canScrollLeft = x > epsilon;
    const canScrollRight = x < maxScroll - epsilon;

    prev.classList.toggle('is-hidden', !canScrollLeft);
    next.classList.toggle('is-hidden', !canScrollRight);

    prev.setAttribute('aria-disabled', canScrollLeft ? 'false' : 'true');
    next.setAttribute('aria-disabled', canScrollRight ? 'false' : 'true');
  }

  renderShell() {
    if (this.querySelector('.cs-cart-upsell__box')) return;
    const heading = this.dataset.csHeading || 'You may also like...';
    this.innerHTML = `
      <div class="cs-cart-upsell__box">
        <div class="cs-cart-upsell__top">
          <div class="cs-cart-upsell__heading">${this.escapeHtml(heading)}</div>
        </div>
        <div class="cs-cart-upsell__slider" role="region" aria-label="You may also like">
          <button type="button" class="cs-cart-upsell__arrow cs-cart-upsell__arrow--prev" aria-label="Previous">
            <svg width="27" height="44" viewBox="0 0 27 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.5001 34.7286C20.5299 34.7286 26.2286 29.0298 26.2286 22C26.2286 14.9702 20.5299 9.27142 13.5001 9.27142C6.47026 9.27142 0.771484 14.9702 0.771484 22C0.771484 29.0298 6.47026 34.7286 13.5001 34.7286Z" fill="white" fill-opacity="0.5"/>
              <path d="M9.22949 21.9491H18.3849" stroke="#1F1F1D" stroke-width="1.27286" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M13.9076 26.6286L8.9738 22.3051C8.9738 22.3051 8.87207 22.2035 8.87207 22.1526C8.87207 22.1018 8.87207 22.0508 8.87207 21.9999C8.87207 21.9491 8.87207 21.8982 8.87207 21.8474C8.87207 21.7966 8.92298 21.7456 8.9738 21.6948L13.9076 17.3714" stroke="#1F1F1D" stroke-width="1.27286" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="cs-cart-upsell__track" role="list"></div>
          <button type="button" class="cs-cart-upsell__arrow cs-cart-upsell__arrow--next" aria-label="Next">
            <svg width="27" height="44" viewBox="0 0 27 44" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.4999 9.27143C6.47015 9.27143 0.771372 14.9702 0.771372 22C0.771372 29.0298 6.47015 34.7286 13.4999 34.7286C20.5297 34.7286 26.2285 29.0298 26.2285 22C26.2285 14.9702 20.5297 9.27143 13.4999 9.27143Z" fill="white" fill-opacity="0.5"/>
              <path d="M17.7706 21.9491H8.61523" stroke="#1F1F1D" stroke-width="1.27286" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M13.0923 26.6286L18.0261 22.3051C18.0261 22.3051 18.1278 22.2035 18.1278 22.1526C18.1278 22.1018 18.1278 22.0508 18.1278 21.9999C18.1278 21.9491 18.1278 21.8982 18.1278 21.8474C18.1278 21.7966 18.0769 21.7456 18.0261 21.6948L13.0923 17.3714" stroke="#1F1F1D" stroke-width="1.27286" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  bind() {
    const track = this.querySelector('.cs-cart-upsell__track');
    if (!track) return;

    // Keep arrows in sync with scroll position (fade in/out)
    const scheduleArrowUpdate = () => {
      if (this._arrowRaf) return;
      this._arrowRaf = requestAnimationFrame(() => {
        this._arrowRaf = null;
        this.updateArrowState();
      });
    };

    // Swipe/drag support (mouse drag-to-scroll; does NOT block normal clicks)
    const state = {
      isPointerDown: false,
      isDragging: false,
      didDrag: false,
      startX: 0,
      scrollLeft: 0,
      pointerId: null,
    };
    const dragThreshold = 10;

    const beginDragging = (e) => {
      state.isDragging = true;
      state.didDrag = true;
      track.classList.add('is-dragging');
      document.body.classList.add('cs-upsell-no-select');
      try {
        track.setPointerCapture(e.pointerId);
      } catch (_) {}
    };

    const onPointerDown = (e) => {
      // only left click / primary touch
      if (e.button != null && e.button !== 0) return;
      // Let native touch scrolling handle mobile swipe; we only implement mouse-drag.
      if (e.pointerType === 'touch') return;

      state.isPointerDown = true;
      state.isDragging = false;
      state.didDrag = false;
      state.pointerId = e.pointerId;
      state.startX = e.clientX;
      state.scrollLeft = track.scrollLeft;
    };

    const onPointerMove = (e) => {
      if (!state.isPointerDown) return;
      const dx = e.clientX - state.startX;

      if (!state.isDragging) {
        if (Math.abs(dx) < dragThreshold) return;
        beginDragging(e);
      }

      track.scrollLeft = state.scrollLeft - dx;
      e.preventDefault();
    };

    const endDrag = () => {
      state.isPointerDown = false;
      state.pointerId = null;
      if (state.isDragging) {
        track.classList.remove('is-dragging');
        document.body.classList.remove('cs-upsell-no-select');
      }
      state.isDragging = false;
      // reset after current tick so click handler can see didDrag
      setTimeout(() => {
        state.didDrag = false;
      }, 0);
    };

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', () => {
      if (state.isDown) endDrag();
    });
    track.addEventListener('scroll', scheduleArrowUpdate, { passive: true });

    // If user dragged, prevent accidental link clicks / text selections
    track.addEventListener(
      'click',
      (e) => {
        if (!state.didDrag) return;
        e.preventDefault();
        e.stopPropagation();
        state.didDrag = false;
      },
      true
    );

    // Extra hard-block for native drag/select behaviors inside the slider.
    track.addEventListener('dragstart', (e) => e.preventDefault());
    track.addEventListener('selectstart', (e) => {
      if (!state.isDragging) return;
      e.preventDefault();
    });

    // Resize changes scrollability; keep arrows updated
    if (window.ResizeObserver) {
      this._ro?.disconnect?.();
      this._ro = new ResizeObserver(() => scheduleArrowUpdate());
      this._ro.observe(track);
    } else {
      window.addEventListener('resize', scheduleArrowUpdate);
    }

    // Initial state
    scheduleArrowUpdate();

    this.addEventListener('click', async (e) => {
      const prev = e.target.closest('.cs-cart-upsell__arrow--prev');
      const next = e.target.closest('.cs-cart-upsell__arrow--next');
      if (prev || next) {
        e.preventDefault();
        const dir = next ? 1 : -1;
        const step = 129 + 10; // item width + gap
        track.scrollBy({ left: dir * step * 2, behavior: 'smooth' });
        return;
      }

      const add = e.target.closest('[data-cs-upsell-add]');
      if (add) {
        e.preventDefault();
        if (add.hasAttribute('disabled')) return;
        const variantId = parseInt(add.dataset.variantId || '', 10);
        if (!variantId) return;

        add.setAttribute('disabled', 'disabled');
        add.setAttribute('aria-busy', 'true');
        this.drawer?.classList?.add('cs-cart-drawer--busy');
        try {
          if (this.drawer?.flushAllPendingNow) {
            await this.drawer.flushAllPendingNow();
          }
          const addReq = this.drawer?.queueMutation
            ? this.drawer.queueMutation(() =>
                fetch('/cart/add.js', {
                  method: 'POST',
                  credentials: 'same-origin',
                  cache: 'no-store',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ id: variantId, quantity: 1 }),
                })
              )
            : fetch('/cart/add.js', {
                method: 'POST',
                credentials: 'same-origin',
                cache: 'no-store',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: variantId, quantity: 1 }),
              });
          await addReq;
          await this.drawer?.refresh?.({ open: true });
        } catch (err) {
          // swallow
        } finally {
          add.removeAttribute('aria-busy');
          add.removeAttribute('disabled');
          this.drawer?.classList?.remove('cs-cart-drawer--busy');
        }
      }
    });
  }

  async load() {
    // If merchant configured a manual list, Liquid already rendered the items.
    if ((this.dataset.csMode || '').toLowerCase() === 'manual') {
      const track = this.querySelector('.cs-cart-upsell__track');
      if (track && track.children.length) {
        this.sortTrackByPrice(track);
        requestAnimationFrame(() => this.updateArrowState());
      } else {
        this.style.display = 'none';
      }
      return;
    }

    const seed = parseInt(this.dataset.csSeedProductId || '', 10);
    if (!seed) return;
    const limit = parseInt(this.dataset.csLimit || '8', 10) || 8;

    const cartProductIds = new Set(
      String(this.dataset.csCartProductIds || '')
        .split(',')
        .map((s) => parseInt(s, 10))
        .filter(Boolean)
    );

    try {
      const url = `/recommendations/products.json?product_id=${seed}&limit=${limit}&intent=related`;
      const res = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const products = Array.isArray(data?.products) ? data.products : [];

      const filtered = products.filter((p) => p && p.id && !cartProductIds.has(p.id));
      if (!filtered.length) {
        this.style.display = 'none';
        return;
      }

      this.renderProducts(filtered.slice(0, limit));
      requestAnimationFrame(() => this.updateArrowState());
    } catch (e) {
      // swallow
    }
  }

  renderProducts(products) {
    const track = this.querySelector('.cs-cart-upsell__track');
    if (!track) return;
    const currency = this.dataset.csCurrency || 'USD';

    track.innerHTML = products
      .map((p) => {
        const variant = (p.variants || []).find((v) => v && v.available) || (p.variants || [])[0] || null;
        if (!variant || !variant.id) return '';

        const imgUrl = this.pickImageUrl(p);
        const image = imgUrl
          ? `<img class="cs-cart-upsell__img" src="${this.escapeHtml(this.withSize(imgUrl, 258, 240))}" alt="${this.escapeHtml(
              p.featured_image?.alt || p.title || ''
            )}" loading="lazy" width="129" height="120" draggable="false">`
          : '';

        const price = this.formatMoney(p.price, currency);
        const compareAt = p.compare_at_price && p.compare_at_price > p.price ? this.formatMoney(p.compare_at_price, currency) : '';

        return `
          <div class="cs-cart-upsell__item" role="listitem" data-cs-price="${p.price}">
            <a class="cs-cart-upsell__link" href="${this.escapeHtml(p.url || `/products/${p.handle}`)}">
              <div class="cs-cart-upsell__imgwrap">${image}</div>
              <div class="cs-cart-upsell__name" title="${this.escapeHtml(p.title || '')}">${this.escapeHtml(p.title || '')}</div>
            </a>
            <div class="cs-cart-upsell__prices">
              <div class="cs-cart-upsell__price">${price}</div>
              ${compareAt ? `<s class="cs-cart-upsell__compare">${compareAt}</s>` : ''}
            </div>
            <button type="button" class="cs-cart-upsell__add" data-cs-upsell-add data-variant-id="${variant.id}">
              <span class="cs-cart-upsell__add-text">Add</span>
              <span class="cs-cart-upsell__add-icon" aria-hidden="true">
                <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="17" height="17" rx="8.5" fill="white"/>
                  <g clip-path="url(#clip0_2040_682)">
                    <path d="M11.9353 8.51946H8.53538V5.11943C8.53538 4.88488 8.34495 4.69446 8.11033 4.69446C7.87578 4.69446 7.68535 4.88488 7.68535 5.11943V8.51946H4.28533C4.05078 8.51946 3.86035 8.70988 3.86035 8.94443C3.86035 9.17906 4.05078 9.36948 4.28533 9.36948H7.68535V12.7694C7.68535 13.0041 7.87578 13.1945 8.11033 13.1945C8.34495 13.1945 8.53538 13.0041 8.53538 12.7694V9.36948H11.9353C12.17 9.36948 12.3604 9.17906 12.3604 8.94443C12.3604 8.70988 12.17 8.51946 11.9353 8.51946Z" fill="black"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_2040_682">
                      <rect width="8.5" height="8.5" fill="white" transform="translate(3.86133 4.69446)"/>
                    </clipPath>
                  </defs>
                </svg>
              </span>
            </button>
          </div>
        `;
      })
      .join('');

    this.sortTrackByPrice(track);

    // Hide arrows if not scrollable
    requestAnimationFrame(() => this.updateArrowState());
  }

  pickImageUrl(p) {
    const fi = p.featured_image;
    if (typeof fi === 'string') return fi;
    if (fi && typeof fi === 'object') return fi.url || fi.src || (Array.isArray(fi) ? fi[0] : '');
    if (Array.isArray(p.images) && p.images[0]) return typeof p.images[0] === 'string' ? p.images[0] : p.images[0].src || p.images[0].url;
    return '';
  }

  withSize(url, width, height) {
    if (!url) return '';
    const joiner = url.includes('?') ? '&' : '?';
    return `${url}${joiner}width=${width}&height=${height}&crop=center`;
  }

  formatMoney(cents, currency) {
    const n = typeof cents === 'number' ? cents : parseInt(cents, 10) || 0;
    const value = n / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
    } catch (e) {
      return `$${value.toFixed(2)}`;
    }
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

customElements.define('cs-cart-upsell', CSCartUpsell);
